/**
 * Раскладка под экранной клавиатурой на всех экранах (CLIENT_WEB.md §3.4 п. 5,
 * CLIENT_IOS_WEBAPP.md §3.2).
 *
 * Android с `interactive-widget=resizes-content` сам уменьшает layout-viewport,
 * и `100dvh` следует за клавиатурой. iOS этого не делает: `100dvh` остаётся
 * прежней, видимая область сокращается, и Safari прокручивает документ (а если
 * документ заперт — сдвигает само видимое окно), чтобы показать поле. Тогда
 * шапка уходит за верхний край, а под содержимым остаётся пустота.
 *
 * Поэтому пока клавиатура открыта:
 *  - `--viewport-height` = высота видимой области: `#root` и страницы входа
 *    сжимаются до неё, шапки остаются на месте;
 *  - `--viewport-offset` = сдвиг видимого окна: на столько же опускается корень
 *    приложения, чтобы шапка была видна, даже если Safari «довернул» страницу;
 *  - `--keyboard-offset` = высота клавиатуры над нижним краем layout-viewport:
 *    на неё поднимаются слои с `position: fixed; bottom` (листы, окна, тосты);
 *  - на `<html>` стоит `data-keyboard` — по нему экраны включают компактную
 *    раскладку (экран входа прячет плитку-иллюстрацию и лишние отступы).
 */
import { isEditableElement, watchKeyboardInset, type KeyboardState } from '@shared/lib/keyboard-inset'

const HEIGHT_PROPERTY = '--viewport-height'
const OFFSET_PROPERTY = '--viewport-offset'
const KEYBOARD_PROPERTY = '--keyboard-offset'
/** Стоит на `<html>`, пока клавиатура открыта, — для стилей, которым важно это знать. */
export const KEYBOARD_ATTR = 'data-keyboard'

/** Пока значения заданы, они перекрывают токены из tokens/scale.css. */
export function applyViewportState(root: HTMLElement, state: KeyboardState): void {
  if (state.inset > 0 && state.height > 0) {
    root.style.setProperty(HEIGHT_PROPERTY, `${state.height}px`)
    root.style.setProperty(OFFSET_PROPERTY, `${state.offsetTop}px`)
    root.style.setProperty(KEYBOARD_PROPERTY, `${state.bottomOverlap}px`)
    root.setAttribute(KEYBOARD_ATTR, '')
    return
  }

  root.style.removeProperty(HEIGHT_PROPERTY)
  root.style.removeProperty(OFFSET_PROPERTY)
  root.style.removeProperty(KEYBOARD_PROPERTY)
  root.removeAttribute(KEYBOARD_ATTR)
}

/** Поле с фокусом — в видимую часть своей прокрутки, когда раскладка уже сжалась. */
function revealActiveElement(): void {
  const active = document.activeElement
  if (!(active instanceof HTMLElement) || typeof active.scrollIntoView !== 'function') return
  active.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

export function startViewportSync(): () => void {
  const root = document.documentElement
  let frame: number | null = null

  // Кадр спустя: раскладка уже пересчитана под новую высоту.
  const scheduleReveal = (): void => {
    if (frame !== null) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = null
      revealActiveElement()
    })
  }

  const stop = watchKeyboardInset((state) => {
    applyViewportState(root, state)
    if (state.inset > 0) scheduleReveal()
  })

  /*
   * Android (`interactive-widget=resizes-content`): layout viewport сжимается сам,
   * но поле, получившее фокус до сжатия, может остаться под краем своей прокрутки —
   * браузер второй раз его не довозит.
   */
  const onWindowResize = (): void => {
    if (isEditableElement(document.activeElement)) scheduleReveal()
  }
  globalThis.addEventListener('resize', onWindowResize)

  return () => {
    stop()
    globalThis.removeEventListener('resize', onWindowResize)
    if (frame !== null) cancelAnimationFrame(frame)
    applyViewportState(root, { inset: 0, height: 0, offsetTop: 0, bottomOverlap: 0 })
  }
}
